import React, { PureComponent } from 'react';
import styled from 'styled-components';

import cn from 'classnames';
import {
  BASE_INPUT,
  INPUT_STATES,
  FOCUSED_INPUT,
  FOCUSED_AREA,
} from './classnames';
import { TextAreaProps, TextAreaState } from './types';

import { baseLighter } from '../styles/colors';

const TextAreaInput = styled.textarea<{ darkMode?: boolean }>`
  ${props =>
    props.darkMode &&
    `
    &::-webkit-input-placeholder {
      color: ${baseLighter};
    }

    &:-moz-placeholder { /* Firefox 18- */
      color: ${baseLighter};
    }

    &::-moz-placeholder {  /* Firefox 19+ */
      color: ${baseLighter};
    }

    &:-ms-input-placeholder {
      color: ${baseLighter};
    }

    &::placeholder {
      color: ${baseLighter};
    }
  `}
`;

class TextArea extends PureComponent<TextAreaProps, TextAreaState> {
  constructor(props: TextAreaProps) {
    super(props);
    this.state = {
      rows: props.rowsMin,
    };
  }

  componentDidUpdate(prevProps: TextAreaProps, prevState: TextAreaState) {
    const { value, onMultirowChange } = this.props;
    const { rows } = this.state;

    if (prevProps.value !== value && this.textAreaEl) {
      this.updateRows(this.textAreaEl);
    }
    if (prevState.rows !== rows) {
      onMultirowChange(rows > 1);
    }
  }

  textAreaEl: HTMLTextAreaElement | null = null;

  updateRows = (textAreaEl: HTMLTextAreaElement) => {
    const { rowsMin, rowsMax } = this.props;

    // reset rows value
    this.setState({ rows: rowsMin }, () => {
      // Get the computed styles for the element
      const computed = window.getComputedStyle(textAreaEl);
      const padding =
        parseInt(computed.getPropertyValue('padding-top'), 10) +
        parseInt(computed.getPropertyValue('padding-bottom'), 10);
      const lineHeight = parseInt(computed.getPropertyValue('line-height'), 10);

      // calculate new rows value based on scrollHeight
      const rows = (textAreaEl.scrollHeight - padding) / lineHeight;
      const newRows = rowsMax && rows > rowsMax ? rowsMax : rows;

      // update state only if necessary
      if (newRows !== this.state.rows && newRows >= rowsMin) {
        this.setState({ rows: newRows });
      }
    });
  };

  onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { onChange, value } = this.props;

    // if the value is not controlled, rows need to be recalculated on change
    if (typeof value === 'undefined') {
      e.persist();
      this.updateRows(e.target);
    }

    onChange(e);
  };

  assignRef = i => {
    const { areaRef } = this.props;

    if (areaRef && typeof areaRef === 'function') {
      areaRef(i);
    } else if (areaRef) {
      areaRef.current = i;
    }
    this.textAreaEl = i;
  };

  render() {
    const {
      'data-testid': dataTestId,
      inputProps,
      styleState,
      white,
      darkMode,
      chat,
      noBorders,
      inputClassName,
      applyFocusedStyles,
      textClassNames,
      /* eslint-disable @typescript-eslint/no-unused-vars */
      areaRef,
      rowsMin,
      rowsMax,
      onMultirowChange,
      onChange,
      /* eslint-enable @typescript-eslint/no-unused-vars */
      ...other
    } = this.props;
    const { rows } = this.state;

    return (
      <TextAreaInput
        darkMode={darkMode}
        className={cn(
          'crehui-textarea crehui-scrollable',
          {
            [rows > 1
              ? FOCUSED_AREA({ white: white || darkMode })
              : FOCUSED_INPUT({
                  white: white || darkMode,
                })]: applyFocusedStyles,
            [chat ? 'px-16 py-8' : 'p-16']: !noBorders,
          },
          BASE_INPUT,
          textClassNames,
          INPUT_STATES[`${styleState}`]({ white: white || darkMode }),
          inputClassName,
        )}
        rows={rows}
        data-testid={dataTestId}
        ref={this.assignRef}
        onChange={this.onChange}
        {...other}
        {...inputProps}
      />
    );
  }
}

export default TextArea;
